import * as signalR from '@microsoft/signalr';
import { APIUrlDefault } from "../utils/configuration";

class SignalRService {
    constructor() {
        this.connection = null;
        this.listeners = {};
        this.isConnecting = false;
        this.registeredMethods = new Set();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 10;
    }

    // Initialize the SignalR connection
    async initConnection() {
        // If connection already exists or is in the process of connecting, return
        if (this.connection || this.isConnecting) {
            return;
        }
        
        this.isConnecting = true;

        try {
            // Create a new connection to the SignalR hub with transport fallback
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(`${APIUrlDefault}notificationHub`, {
                    // Try all transports, starting with WebSockets but allowing fallback
                    transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling,
                    skipNegotiation: false
                })
                .withAutomaticReconnect([0, 1000, 2000, 5000, 10000, 15000, 30000])
                .configureLogging(signalR.LogLevel.Information)
                .build();

            // Handle reconnection events
            this.connection.onreconnecting(error => {
                console.log('SignalR connection lost. Attempting to reconnect...', error);
                this.isConnecting = true;
            });

            this.connection.onreconnected(connectionId => {
                console.log('SignalR connection reestablished:', connectionId);
                this.isConnecting = false;
                this.reconnectAttempts = 0;
            });

            // Handle connection closed
            this.connection.onclose(error => {
                console.log('SignalR connection closed:', error);
                this.isConnecting = false;
                
                // Try to reconnect
                if (this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.reconnectAttempts++;
                    const delay = Math.min(1000 * Math.pow(1.5, this.reconnectAttempts), 30000);
                    console.log(`Will attempt to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
                    
                    setTimeout(() => {
                        try {
                            // Ensure we clean up any existing connection
                            if (this.connection) {
                                // Remove all event handlers
                                for (const methodName of this.registeredMethods) {
                                    try { this.connection.off(methodName); } catch (e) { }
                                }
                            }
                        } catch (e) { }
                        
                        this.connection = null;
                        this.initConnection();
                    }, delay);
                } else {
                    console.log('Maximum reconnection attempts reached. Please refresh the page.');
                }
            });

            // Start the connection with timeout handling
            const startPromise = this.connection.start();
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Connection start timed out')), 15000);
            });
            
            await Promise.race([startPromise, timeoutPromise]);
            console.log('SignalR connection established successfully');
            this.reconnectAttempts = 0;
            this.isConnecting = false;
        } catch (error) {
            console.error('Error establishing SignalR connection:', error);
            this.connection = null;
            this.isConnecting = false;
            
            // Try to reconnect with exponential backoff
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
                this.reconnectAttempts++;
                const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
                console.log(`Will attempt to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
                
                setTimeout(() => this.initConnection(), delay);
            }
        }
    }

    // Add event listener for a specific method
    addListener(methodName, callback) {
        // Normalize method name to lowercase to avoid case sensitivity issues
        const normalizedMethodName = methodName.toLowerCase();
        
        // Initialize connection if it doesn't exist
        if (!this.connection) {
            this.initConnection().then(() => {
                // After connection is established, try adding the listener again
                this.addListener(normalizedMethodName, callback);
            });
            return;
        }

        // Initialize the listeners array for this method if it doesn't exist
        if (!this.listeners[normalizedMethodName]) {
            this.listeners[normalizedMethodName] = [];
            
            // Only register the method with SignalR once per method name
            if (!this.registeredMethods.has(normalizedMethodName)) {
                try {
                    this.connection.on(normalizedMethodName, (data) => {
                        console.log(`Received ${normalizedMethodName} notification:`, data);
                        
                        // Call all registered callbacks for this method
                        if (this.listeners[normalizedMethodName]) {
                            this.listeners[normalizedMethodName].forEach(cb => {
                                try {
                                    cb(data);
                                } catch (callbackError) {
                                    console.error(`Error in ${normalizedMethodName} callback:`, callbackError);
                                }
                            });
                        }
                    });
                    
                    this.registeredMethods.add(normalizedMethodName);
                    console.log(`Registered SignalR method: ${normalizedMethodName}`);
                } catch (error) {
                    console.error(`Error registering SignalR method ${normalizedMethodName}:`, error);
                }
            }
        }
        
        // Check if this callback is already registered to avoid duplicates
        const isCallbackRegistered = this.listeners[normalizedMethodName].some(
            existingCallback => existingCallback.toString() === callback.toString()
        );
        
        // Only add the callback if it's not already registered
        if (!isCallbackRegistered) {
            this.listeners[normalizedMethodName].push(callback);
            console.log(`Added listener for ${normalizedMethodName}`);
        }
    }

    // Remove event listener for a specific method
    removeListener(methodName, callback) {
        // Normalize method name to lowercase to avoid case sensitivity issues
        const normalizedMethodName = methodName.toLowerCase();
        
        if (!this.connection || !this.listeners[normalizedMethodName]) {
            return;
        }
        
        // Remove the callback from our listeners object
        this.listeners[normalizedMethodName] = this.listeners[normalizedMethodName].filter(
            cb => cb !== callback
        );
        
        console.log(`Removed listener for ${normalizedMethodName}`);

        // If no more listeners for this method, remove the SignalR handler
        if (this.listeners[normalizedMethodName].length === 0) {
            try {
                this.connection.off(normalizedMethodName);
                delete this.listeners[normalizedMethodName];
                this.registeredMethods.delete(normalizedMethodName);
                console.log(`Removed SignalR handler for ${normalizedMethodName}`);
            } catch (error) {
                console.error(`Error removing SignalR handler for ${normalizedMethodName}:`, error);
            }
        }
    }

    // Stop the SignalR connection
    async stopConnection() {
        if (this.connection) {
            try {
                // Remove all listeners first
                for (const methodName of this.registeredMethods) {
                    try {
                        this.connection.off(methodName);
                    } catch (error) {
                        console.error(`Error removing SignalR handler for ${methodName}:`, error);
                    }
                }
                
                // Stop the connection
                await this.connection.stop();
                console.log('SignalR connection stopped');
                
                // Reset state
                this.connection = null;
                this.listeners = {};
                this.registeredMethods.clear();
                this.isConnecting = false;
                this.reconnectAttempts = 0;
            } catch (error) {
                console.error('Error stopping SignalR connection:', error);
            }
        }
    }
}

// Create a singleton instance
const signalRService = new SignalRService();
export default signalRService;